/**
 * Browser-side access to the Worker's /api/todoist/* routes.
 *
 * Those routes require a signed-in app user (the Worker validates the token
 * against PocketBase), so every call has to carry the PocketBase session token.
 * Centralised here so no caller forgets it and gets a confusing 401.
 *
 * The Todoist token itself is never involved on this side — it lives only as a
 * Worker secret.
 */

import { pb, appMode } from "./pocketbase";

export interface TodoistPing {
  ok: true;
  email: string;
  name: string;
}

/** Why a connection check can't even be attempted, or null if it can. */
export function todoistUnavailableReason(): string | null {
  if (appMode === "demo") return "Not available in demo mode — sign in to your own data first.";
  if (!pb?.authStore?.token) return "Sign in first.";
  return null;
}

export async function todoistGet<T>(route: string): Promise<T> {
  const token = pb?.authStore?.token;
  if (!token) throw new Error("Sign in first — no PocketBase session in this browser.");

  const res = await fetch(`/api/todoist/${route}`, { headers: { Authorization: token } });
  const body: unknown = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body as T;
}

export const pingTodoist = () => todoistGet<TodoistPing>("ping");

async function todoistPost<T = void>(route: string): Promise<T> {
  const token = pb?.authStore?.token;
  if (!token) throw new Error("Sign in first — no PocketBase session in this browser.");

  const res = await fetch(`/api/todoist/${route}`, {
    method: "POST",
    headers: { Authorization: token },
  });
  const body: unknown = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  return body as T;
}

/**
 * Project Hub writes only two things to Todoist: new tasks (Quick Add) and
 * completion. Editing — renaming, re-dating, deleting — stays in Todoist, which
 * is why the mirror can treat itself as a projection and never has to merge:
 * even a task added here arrives in PocketBase through the sync.
 */
/** Capture into Todoist. Omit the project and it lands in the Todoist Inbox. */
export const addTodoistTask = (content: string, projectTodoistId?: string) =>
  todoistPost<{ ok: true; id: string }>(
    `tasks?content=${encodeURIComponent(content)}` +
    (projectTodoistId ? `&project_id=${encodeURIComponent(projectTodoistId)}` : ""),
  );

/**
 * Quick Add, parsed by Todoist: dates, #project, /section, @labels, p1–p4.
 * `into` is where you added from — used only when the text names no project.
 */
export const quickAddTodoistTask = (text: string, into?: { projectId?: string; sectionId?: string }) =>
  todoistPost<{ ok: true; id: string; content: string; due: string | null }>(
    `tasks/quick?text=${encodeURIComponent(text)}` +
    (into?.projectId ? `&project_id=${encodeURIComponent(into.projectId)}` : "") +
    (into?.sectionId ? `&section_id=${encodeURIComponent(into.sectionId)}` : ""),
  );

export const closeTodoistTask = (todoistId: string) => todoistPost(`tasks/${todoistId}/close`);
export const reopenTodoistTask = (todoistId: string) => todoistPost(`tasks/${todoistId}/reopen`);
